import { PlatformIcon } from '@/components/PlatformIcon';
import type { PlatformTypes } from '@/types/gql.generated';
import { Box, Flex, Grid, Text } from '@/ui';
import { getShortName } from '@/utils/platformLib';

type Props = {
  platforms: PlatformTypes[];
  selectedPlatform: PlatformTypes | null;
  onChange: (platform: PlatformTypes) => void;
};

const PlatformTile = ({
  platform,
  isSelected,
  isInactive,
  onClick,
}: {
  platform: PlatformTypes;
  isSelected: boolean;
  isInactive: boolean;
  onClick: () => void;
}) => {
  return (
    <Box
      _hover={isSelected ? undefined : { borderColor: 'ink.soft' }}
      aria-label={getShortName(platform)}
      aria-pressed={isSelected}
      as="button"
      bg="white"
      borderColor={isSelected ? 'brand.500' : 'line.card'}
      borderRadius="ah-lg"
      borderStyle="solid"
      borderWidth="1px"
      boxShadow={isSelected ? 'ah-ring-selected' : undefined}
      opacity={isInactive ? 0.45 : 1}
      transition="border-color .15s, box-shadow .15s, opacity .15s"
      type="button"
      onClick={onClick}
    >
      <Flex align="center" direction="column" gap={1} px={1} py={2.5}>
        <PlatformIcon platform={platform} size={12} />
        <Text
          color="ink.strong"
          fontSize="ah-micro"
          fontWeight={600}
          maxW="100%"
          noOfLines={1}
        >
          {/* Short chip labels so all tiles fit on one row; full names are
              used in the description line and the footer CTA. */}
          {getShortName(platform)}
        </Text>
      </Flex>
    </Box>
  );
};

export const ExportPlatformPicker = ({
  platforms,
  selectedPlatform,
  onChange,
}: Props) => (
  <Grid gap={1.5} templateColumns={`repeat(${platforms.length}, 1fr)`}>
    {platforms.map((platform) => (
      <PlatformTile
        isInactive={selectedPlatform !== null && selectedPlatform !== platform}
        isSelected={selectedPlatform === platform}
        key={platform}
        platform={platform}
        onClick={() => onChange(platform)}
      />
    ))}
  </Grid>
);
